import { Badge, Skeleton } from "@/components/kit";
import type { OrderListItemDTO } from "@/modules/orders/domain/types";
import {
  formatDeliverySummary,
  getOrderStatusMeta,
  PAYMENT_TYPE_META,
  orderStatusLabel,
} from "@/modules/orders/presentation/ui/order-status";
import { formatLak } from "@/shared/lib/currency";
import { formatDateTimeLocal } from "@/shared/lib/date-time";

type FulfillmentTableProps = {
  orders: OrderListItemDTO[];
  isLoading?: boolean;
  onRowClick: (id: string) => void;
};

export function FulfillmentTable({
  orders,
  isLoading,
  onRowClick,
}: FulfillmentTableProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded-lg" />
        ))}
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <p className="py-12 text-center text-muted-foreground text-sm">
        ບໍ່ມີອໍເດີ້ໃນການກັ່ນຕອງນີ້
      </p>
    );
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-secondary/20">
      <table className="w-full min-w-[720px] text-sm">
        <thead>
          <tr className="border-secondary/20 border-b bg-muted/40 text-left text-muted-foreground text-xs">
            <th className="px-4 py-3 font-medium">ອໍເດີ້</th>
            <th className="px-4 py-3 font-medium">ລູກຄ້າ</th>
            <th className="px-4 py-3 font-medium">ສະຖານະ</th>
            <th className="px-4 py-3 font-medium">ຮັບ/ສົ່ງ</th>
            <th className="px-4 py-3 font-medium">ລາຍການ</th>
            <th className="px-4 py-3 font-medium text-right">ຍອດ</th>
            <th className="px-4 py-3 font-medium">ເວລາ</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => {
            const meta = getOrderStatusMeta(order.status, order.paymentType);
            return (
              <tr
                key={order.id}
                className="cursor-pointer border-secondary/10 border-b transition-colors last:border-0 hover:bg-secondary/5"
                onClick={() => onRowClick(order.id)}
              >
                <td className="px-4 py-3 font-mono text-xs">
                  {order.orderNumber}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col">
                    <span className="font-medium">{order.customerName}</span>
                    {order.customerPhone ? (
                      <span className="text-muted-foreground text-xs">
                        {order.customerPhone}
                      </span>
                    ) : null}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-wrap gap-1">
                    <Badge variant={meta.variant}>
                      {orderStatusLabel(order.status, order.paymentType)}
                    </Badge>
                    <Badge
                      variant={PAYMENT_TYPE_META[order.paymentType].variant}
                    >
                      {PAYMENT_TYPE_META[order.paymentType].shortLabel}
                    </Badge>
                  </div>
                </td>
                <td className="max-w-[180px] truncate px-4 py-3 text-muted-foreground">
                  {formatDeliverySummary(order)}
                </td>
                <td className="px-4 py-3 tabular-nums">{order.itemCount}</td>
                <td className="px-4 py-3 text-right font-medium tabular-nums">
                  {formatLak(order.total)}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-xs">
                  {formatDateTimeLocal(order.createdAt)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
